import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  fetchWalletEmails,
  updateUseFor,
  removeEmail,
  validateEmail,
  addEmail,
  verifyOtp,
  setInlineMode,
  clearInline,
  cancelOtp,
  resendOtp,
  dismissVerifiedNotification,
  resendVerification,
  pollVerificationStatus,
  setDefaultEmail,
  clearDefaultEmailError,
  clearAddError,
  CLEAR_REMOVE_ERROR,
  CLEAR_CLASSIFICATION,
} from '../actions/walletEmails';
import { selectDefaultEmailPending } from '../reducers/walletEmails';
import { trackEvent } from '../utils/analytics';
import { sendToSentry } from '../utils/error';
import WalletSectionCard from '../components/profile/WalletSectionCard';
import WalletAlert from '../components/profile/WalletAlert';
import EmailTable from '../components/profile/emails/EmailTable';
import RemoveEmailConfirmModal from '../components/profile/emails/RemoveEmailConfirmModal';
import AddEmailRow, {
  ADD_EMAIL_ERROR_ID,
  requiresInputEdit,
} from '../components/profile/emails/AddEmailRow';
import InlineOtpPanel from '../components/profile/emails/InlineOtpPanel';
import EmailVerifiedNotification from '../components/profile/emails/EmailVerifiedNotification';
import BlockedDomainAlert from '../components/profile/emails/BlockedDomainAlert';
import EmailCollisionAlert from '../components/profile/emails/EmailCollisionAlert';
import WalletMergeWidget from '../components/profile/emails/WalletMergeWidget';
import { hasMountableRunner } from '../components/workflow/WorkflowWidget';
import SuspiciousDomainPrompt from '../components/profile/emails/SuspiciousDomainPrompt';
import WalletLockedModal from '../components/profile/emails/WalletLockedModal';
import { FragnoButton } from 'fragno/react';
import walletEmailsStyle from '../style/components/profile/WalletEmails.module.scss';

// Pick a translation key for the set-default error banner based on the
// server's errorCode and whether the failure followed a successful OTP
// verification. The server's raw message references the legacy "primary"
// wording; we render our own "default" copy keyed off the structured
// errorCode and never display the server string.
const defaultEmailErrorKey = ({ errorCode, afterVerification }) => {
  if (afterVerification)
    return 'walletEmails.defaultEmailErrorAfterVerification';
  switch (errorCode) {
    case 'UNCONFIRMED_EMAIL':
      return 'walletEmails.defaultEmailErrorUnconfirmed';
    case 'ALREADY_DEFAULT':
      return 'walletEmails.defaultEmailErrorAlreadyDefault';
    case 'EMAIL_NOT_FOUND':
      return 'walletEmails.defaultEmailErrorNotFound';
    case 'EMAIL_NOT_LINKED':
      return 'walletEmails.defaultEmailErrorNotLinked';
    default:
      return 'walletEmails.defaultEmailErrorGeneric';
  }
};

// Pick a translation key for the inline error rendered beneath the add-email
// row. `INVALID_EMAIL` is synthesized client-side when the validate endpoint
// returns `{ valid: false }`; `VALIDATION_ERROR` (422 — "Invalid email
// format." and similar post-submit rejections) and `ALREADY_PENDING` come
// from the addEmail endpoint. Anything else (5xx, network, an unknown
// errorCode) falls through to a generic message.
// AXP-1613: ALREADY_EXISTS no longer comes from addEmail. The entity may
// already exist server-side at Submit time for the non-collision case (see
// actions/walletEmails.js#addEmail), but a collision is never revealed until
// OTP succeeds — see the 'collision' inline mode for that case instead.
// ALREADY_PENDING and ALREADY_VERIFIED are both non-collision cases: this
// same wallet already has an entity for the address, unconfirmed or
// confirmed respectively (e.g. an earlier attempt that was never completed,
// or the address is already in the list and verified) — safe to state
// directly either way, since both describe the requester's own wallet, not
// a collision with anyone else's.
const addEmailErrorKey = ({ errorCode }) => {
  switch (errorCode) {
    case 'INVALID_EMAIL':
    case 'VALIDATION_ERROR':
      return 'walletEmails.addEmailErrorInvalid';
    case 'ADD_EMAIL_RATE_LIMITED':
      return 'walletEmails.addEmailErrorRateLimited';
    case 'ADD_EMAIL_LOCKED':
      return 'walletEmails.addEmailErrorLocked';
    case 'ALREADY_PENDING':
      return 'walletEmails.addEmailErrorPending';
    case 'ALREADY_VERIFIED':
      return 'walletEmails.addEmailErrorVerified';
    default:
      return 'walletEmails.addEmailErrorGeneric';
  }
};

// Client-side abandonment event for the inline OTP funnel (AXP-1579): there's
// no server signal for "abandoned," so we emit one per OTP session.
const OTP_ANALYTICS_CATEGORY = 'Wallet Email OTP';
const OTP_ABANDONED_ACTION = 'OTP Abandoned';
// Abandonment reasons (event label); first-wins per session.
const OTP_ABANDON_CANCEL = 'cancel';
const OTP_ABANDON_TIMEOUT = 'timeout';
const OTP_ABANDON_NAVIGATION = 'navigation';

const WalletEmails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    emails,
    isFetching,
    fetchError,
    removeError,
    isRemoving,
    inline,
    justVerifiedEmailId,
    defaultEmailError,
  } = useSelector((state) => state.walletEmails);

  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [emailToRemove, setEmailToRemove] = useState(null);
  const [preCheckErrorCode, setPreCheckErrorCode] = useState(null);
  const [preCheckIsOnlyWork, setPreCheckIsOnlyWork] = useState(false);
  const pendingRemoveId = useRef(null);
  const [addedAddress, setAddedAddress] = useState('');
  const [addedUseFor, setAddedUseFor] = useState('');

  // --- OTP abandonment analytics (AXP-1579) ---
  // Record at most one abandonment per OTP session; reset on a new session.
  const otpAbandonmentRecordedRef = useRef(false);
  // Latest inline.mode for the unmount cleanup (avoids a stale closure); still
  // 'otp' at unmount means the user navigated away mid-verification.
  const inlineModeRef = useRef(inline.mode);
  // inspection_id + session-start clock for the abandonment event. Held in refs
  // so recordOtpAbandonment stays referentially stable (the unmount cleanup
  // below depends on it) while still reading the current session's values.
  const otpVerificationIdRef = useRef(inline.verificationId);
  const otpStartTimeRef = useRef(null);

  const recordOtpAbandonment = useCallback((label) => {
    if (otpAbandonmentRecordedRef.current) return;
    otpAbandonmentRecordedRef.current = true;
    const startedAt = otpStartTimeRef.current;
    trackEvent({
      category: OTP_ANALYTICS_CATEGORY,
      action: OTP_ABANDONED_ACTION,
      label,
      inspection_id: otpVerificationIdRef.current,
      duration_seconds: startedAt
        ? Math.round((Date.now() - startedAt) / 1000)
        : undefined,
    });
  }, []);

  useEffect(() => {
    otpVerificationIdRef.current = inline.verificationId;
  }, [inline.verificationId]);

  useEffect(() => {
    inlineModeRef.current = inline.mode;
    // A new OTP session started — allow this session to record an abandonment
    // and stamp the clock the duration is measured from.
    if (inline.mode === 'otp') {
      otpAbandonmentRecordedRef.current = false;
      otpStartTimeRef.current = Date.now();
    }
  }, [inline.mode]);

  // Navigate-away abandonment: the whole emails section unmounts (route change)
  // while an OTP verification is still pending. recordOtpAbandonment is stable,
  // so this cleanup runs only on unmount.
  useEffect(
    () => () => {
      if (inlineModeRef.current === 'otp') {
        recordOtpAbandonment(OTP_ABANDON_NAVIGATION);
      }
    },
    [recordOtpAbandonment],
  );

  useEffect(() => {
    dispatch(fetchWalletEmails());
  }, [dispatch]);

  const handleRetryFetch = useCallback(() => {
    dispatch(fetchWalletEmails());
  }, [dispatch]);

  // Handle server-side remove result: close modal on success, show error on failure.
  // Watches isRemoving (driven by REMOVE_EMAIL_SUCCESS/FAILURE) rather than the
  // emails array, avoiding a race with concurrent list fetches.
  useEffect(() => {
    if (!pendingRemoveId.current) return;
    if (isRemoving) return;

    if (removeError?.emailId === pendingRemoveId.current) {
      pendingRemoveId.current = null;
      return;
    }
    // A stale or non-matching error must not drive the modal closed.
    if (removeError) return;

    setRemoveModalOpen(false);
    setEmailToRemove(null);
    pendingRemoveId.current = null;
  }, [isRemoving, removeError]);

  // --- Use for ---
  const handleUseForChange = useCallback(
    (emailId, useFor) => {
      dispatch(updateUseFor(emailId, useFor));
    },
    [dispatch],
  );

  // --- Remove flow ---
  // `type` is the platform's email classification: 'personal', 'work',
  // 'unknown', or 'malicious'. Last-of-type warnings fire only for
  // 'personal' and 'work'; 'unknown' and 'malicious' aren't last-of-type
  // categories.
  const hasType = useCallback(
    (typeValue) => (email) => email.type === typeValue,
    [],
  );

  const isLastPersonalEmail = useCallback(
    (email) => {
      const isPersonal = hasType('personal');
      if (!isPersonal(email)) return false;
      return emails.filter(isPersonal).length <= 1;
    },
    [emails, hasType],
  );

  const isOnlyWorkEmail = useCallback(
    (email) => {
      const isWork = hasType('work');
      if (!isWork(email)) return false;
      return emails.filter(isWork).length <= 1;
    },
    [emails, hasType],
  );

  const handleRemove = useCallback(
    (email) => {
      const isVerified = (e) =>
        e.verification_status === 'verified' ||
        e.verification_status === 'verified_pending_review';
      const otherVerified = emails.filter(
        (e) => e.email_id !== email.email_id && isVerified(e),
      );

      let nextCode = null;
      if (emails.length <= 1) {
        nextCode = 'LAST_EMAIL';
      } else if (isVerified(email) && otherVerified.length === 0) {
        nextCode = 'LAST_VERIFIED';
      } else if (isLastPersonalEmail(email)) {
        nextCode = 'LAST_PERSONAL';
      } else if (email.is_default) {
        nextCode = 'DEFAULT_EMAIL';
      }

      // This fires from FragnoActionMenu's fragno-action-menu-select
      // CustomEvent listener — a native addEventListener callback, not a React
      // SyntheticEvent — so React 17 does not auto-batch the setState calls
      // below. Batch them explicitly so email/errorCode/isOpen always land
      // in a single consistent render rather than momentarily-mismatched
      // intermediate ones.
      ReactDOM.unstable_batchedUpdates(() => {
        setEmailToRemove(email);
        // Snapshot the work-only status at click time so a concurrent emails
        // refetch can't flip the warning copy out from under the open modal.
        setPreCheckIsOnlyWork(isOnlyWorkEmail(email));
        setPreCheckErrorCode(nextCode);
        setRemoveModalOpen(true);
      });
    },
    [emails, isLastPersonalEmail, isOnlyWorkEmail],
  );

  const handleConfirmRemove = useCallback(
    (emailId, { newDefaultId } = {}) => {
      pendingRemoveId.current = emailId;
      dispatch(removeEmail(emailId, { newDefaultId }));
    },
    [dispatch],
  );

  const handleCloseRemoveModal = useCallback(() => {
    setRemoveModalOpen(false);
    setEmailToRemove(null);
    setPreCheckErrorCode(null);
    setPreCheckIsOnlyWork(false);
    pendingRemoveId.current = null;
    dispatch({ type: CLEAR_REMOVE_ERROR });
  }, [dispatch]);

  const hasRemoveError = removeError?.emailId === emailToRemove?.email_id;
  const errorCode =
    preCheckErrorCode ||
    (hasRemoveError ? removeError?.errorCode || 'UNKNOWN_ERROR' : null);

  // --- Add email flow ---
  const handleStartAdd = useCallback(() => {
    dispatch(setInlineMode('add'));
  }, [dispatch]);

  const handleCancelAdd = useCallback(() => {
    // Cancelling (or closing) the OTP panel is an explicit abandonment,
    // regardless of whether it's an add or a resend-verification session.
    if (inline.mode === 'otp') {
      recordOtpAbandonment(OTP_ABANDON_CANCEL);
    }
    // When canceling from OTP mode, keep the email as "not_verified"
    if (inline.mode === 'otp' && !inline.targetEmailId) {
      dispatch(cancelOtp());
    } else {
      // Resend verification cancel or non-OTP cancel — email already in list
      dispatch(clearInline());
    }
    setAddedAddress('');
    setAddedUseFor('');
  }, [dispatch, inline.mode, inline.targetEmailId, recordOtpAbandonment]);

  const handleClearClassification = useCallback(() => {
    dispatch({ type: CLEAR_CLASSIFICATION });
  }, [dispatch]);

  const handleClearAddError = useCallback(() => {
    dispatch(clearAddError());
  }, [dispatch]);

  const handleValidate = useCallback(
    (address) => {
      dispatch(validateEmail(address));
    },
    [dispatch],
  );

  const handleSubmitAdd = useCallback(
    (address, useFor) => {
      setAddedAddress(address);
      setAddedUseFor(useFor);
      if (inline.classification === 'unknown') {
        dispatch(setInlineMode('suspicious'));
        return;
      }
      dispatch(addEmail(address, useFor));
    },
    [dispatch, inline.classification],
  );

  const handleSuspiciousContinue = useCallback(() => {
    dispatch(addEmail(addedAddress, addedUseFor));
  }, [dispatch, addedAddress, addedUseFor]);

  const handleTryDifferent = useCallback(() => {
    dispatch(clearInline());
    dispatch(setInlineMode('add'));
  }, [dispatch]);

  // On widget failure, report it and let the render fall back to the collision
  // alert + table so the user isn't stranded; exact error UX comes later.
  const [widgetFailed, setWidgetFailed] = useState(false);
  const handleWalletMergeError = useCallback((err) => {
    sendToSentry(err);
    setWidgetFailed(true);
  }, []);
  // Reset per collision so a later one gets a fresh mount attempt.
  useEffect(() => {
    setWidgetFailed(false);
  }, [inline.mergeWidgetConfig?.collisionEmail]);

  const handleWalletMergeExit = useCallback(() => {
    dispatch(clearInline());
  }, [dispatch]);

  // The runner calls onComplete on every terminal status (COMPLETED, FAILED,
  // CANCELLED). Only a success tears the widget down; on failure or cancel it
  // stays mounted to render its own screen, whose Done button fires onExit.
  const handleWalletMergeComplete = useCallback(
    (state) => {
      if (state?.status !== 'COMPLETED') {
        return;
      }
      dispatch(clearInline());
      dispatch(fetchWalletEmails());
    },
    [dispatch],
  );

  // --- OTP flow ---
  const handleVerifyOtp = useCallback(
    (code) => {
      dispatch(verifyOtp(inline.verificationId, code, inline.intent));
    },
    [dispatch, inline.verificationId, inline.intent],
  );

  const handleResendOtp = useCallback(() => {
    dispatch(resendOtp(inline.verificationId));
  }, [dispatch, inline.verificationId]);

  const handlePoll = useCallback(
    (signal) => {
      dispatch(pollVerificationStatus(inline.verificationId, signal));
    },
    [dispatch, inline.verificationId],
  );

  // Fired once by InlineOtpPanel when its 5-minute countdown expires.
  const handleOtpExpire = useCallback(() => {
    recordOtpAbandonment(OTP_ABANDON_TIMEOUT);
  }, [recordOtpAbandonment]);

  const handleResendVerification = useCallback(
    (email) => {
      // Open OTP panel targeting this email's row and trigger resend via thunk
      setAddedAddress(email.address);
      // Default classification to 'personal' for legacy rows that lack a type field —
      // an undefined classification breaks the verified-email status branch in
      // VERIFY_OTP_SUCCESS (state.inline.classification === 'unknown').
      dispatch(resendVerification(email.email_id, email.type || 'personal'));
    },
    [dispatch],
  );

  const defaultEmailPending = useSelector(selectDefaultEmailPending);

  const handleSetDefault = useCallback(
    (email) => {
      // Guard against double-tap races — a second BEGIN would corrupt the
      // _previous_is_default snapshot used by FAILURE revert.
      if (defaultEmailPending) return;
      if (
        email.verification_status === 'verified' ||
        email.verification_status === 'verified_pending_review'
      ) {
        dispatch(setDefaultEmail(email.email_id));
      } else {
        // Unverified email: open the OTP panel with intent='set_default';
        // VERIFY_OTP_SUCCESS (or polling success) will chain setDefaultEmail.
        setAddedAddress(email.address);
        dispatch(
          resendVerification(
            email.email_id,
            email.type || 'personal',
            'set_default',
          ),
        );
      }
    },
    [dispatch, defaultEmailPending],
  );

  const handleContactSupport = useCallback(() => {
    window.location.assign('https://help.id.me');
  }, []);

  const handleCloseLockedModal = useCallback(() => {
    // Session already destroyed server-side — force refresh to trigger sign-in redirect
    window.location.assign('/overview');
  }, []);

  const handleDismissVerifiedNotification = useCallback(() => {
    dispatch(dismissVerifiedNotification());
  }, [dispatch]);

  const handleDismissDefaultEmailError = useCallback(() => {
    dispatch(clearDefaultEmailError());
  }, [dispatch]);

  // Helper to wrap inline content in a table row. `withSeparator` adds a
  // 1px bottom border to the cell so the card visually divides from the
  // email row below it — used for the verified notification when it sits
  // between rows in the table (e.g. Resend Verification flow).
  const inlineTableRow = (content, key, { withSeparator = false } = {}) => (
    <tr key={key} className={walletEmailsStyle.inlineRow}>
      <td
        colSpan="5"
        className={
          withSeparator
            ? walletEmailsStyle.inlineRowCellWithSeparator
            : walletEmailsStyle.inlineRowCell
        }
      >
        {content}
      </td>
    </tr>
  );

  // AXP-1580: a failed list load used to render as a table with headers and no
  // rows, which reads as "you have no emails" when the truth is that we never
  // received the list. Hide the table and let the error banner speak for it —
  // except while an inline flow is open, since AddEmailRow and the OTP panel
  // render inside the table.
  const listUnavailable = !!fetchError && emails.length === 0 && !inline.mode;

  const hasCollision = inline.mode === 'collision';
  const hasMountableMergeManifest = hasMountableRunner(
    inline.mergeWidgetConfig?.widgetManifest,
  );
  const showMergeWidget =
    hasCollision && hasMountableMergeManifest && !widgetFailed;

  // Inline content that renders inside the table as a row
  const renderAddRowElement = () => {
    switch (inline.mode) {
      case 'add':
        return (
          <>
            <AddEmailRow
              onValidate={handleValidate}
              onSubmit={handleSubmitAdd}
              onCancel={handleCancelAdd}
              onClearClassification={handleClearClassification}
              onClearAddError={handleClearAddError}
              classification={inline.classification}
              domain={inline.domain}
              isSubmitting={inline.isSubmitting}
              isValidating={inline.isValidating}
              addError={inline.addError}
            />
            {inline.addError &&
              inlineTableRow(
                <div id={ADD_EMAIL_ERROR_ID}>
                  <WalletAlert variant="error">
                    {t(addEmailErrorKey(inline.addError))}
                  </WalletAlert>
                </div>,
                'add-error',
              )}
          </>
        );
      case 'otp':
        // OTP panel is rendered via otpRowElement on EmailTable
        return null;
      case 'suspicious':
        // The unknown-domain flow dispatches addEmail from the Continue
        // button. If that fails (e.g. 422 VALIDATION_ERROR) the addError
        // lands in state but mode stays 'suspicious' — render the same
        // WalletAlert here so the user actually sees the result, and
        // gate Continue for errors that require editing the address.
        return (
          <>
            {inlineTableRow(
              <SuspiciousDomainPrompt
                onContinue={handleSuspiciousContinue}
                onTryDifferent={handleTryDifferent}
                isSubmitting={inline.isSubmitting}
                continueDisabled={requiresInputEdit(inline.addError)}
              />,
              'suspicious-prompt',
            )}
            {inline.addError &&
              inlineTableRow(
                <div id={ADD_EMAIL_ERROR_ID}>
                  <WalletAlert variant="error">
                    {t(addEmailErrorKey(inline.addError))}
                  </WalletAlert>
                </div>,
                'add-error-suspicious',
              )}
          </>
        );
      case 'blocked':
        return inlineTableRow(
          <BlockedDomainAlert
            attemptsRemaining={inline.attemptsRemaining}
            onContactSupport={handleContactSupport}
            onTryDifferent={handleTryDifferent}
          />,
        );
      case 'collision':
        return inlineTableRow(
          <EmailCollisionAlert
            address={inline.pendingEmail?.address}
            onTryDifferent={handleTryDifferent}
          />,
        );
      default:
        return null;
    }
  };

  const renderMainContent = () => {
    if (isFetching || listUnavailable) return null;

    // While mounted, the widget stands in for the whole table + collision-alert
    // surface (so renderAddRowElement's 'collision' case is bypassed); on
    // failure the table + EmailCollisionAlert below become the exit.
    if (showMergeWidget) {
      return (
        <WalletMergeWidget
          mergeWidgetConfig={inline.mergeWidgetConfig}
          onExit={handleWalletMergeExit}
          onComplete={handleWalletMergeComplete}
          onError={handleWalletMergeError}
        />
      );
    }

    return (
      <EmailTable
        emails={emails}
        onUseForChange={handleUseForChange}
        onRemove={handleRemove}
        onResendVerification={handleResendVerification}
        onSetDefault={handleSetDefault}
        defaultEmailPending={defaultEmailPending}
        addRowElement={renderAddRowElement()}
        otpTargetEmailId={inline.targetEmailId}
        otpRowElement={
          inline.mode === 'otp'
            ? inlineTableRow(
                <InlineOtpPanel
                  emailAddress={addedAddress}
                  otpError={inline.otpError}
                  onCancel={handleCancelAdd}
                  onResend={handleResendOtp}
                  onComplete={handleVerifyOtp}
                  verificationId={inline.verificationId}
                  onPoll={handlePoll}
                  onExpire={handleOtpExpire}
                />,
                inline.targetEmailId || 'otp-new',
              )
            : null
        }
        justVerifiedEmailId={justVerifiedEmailId}
        verifiedNotificationElement={
          justVerifiedEmailId
            ? inlineTableRow(
                <EmailVerifiedNotification
                  onDismiss={handleDismissVerifiedNotification}
                />,
                `verified-${justVerifiedEmailId}`,
                { withSeparator: true },
              )
            : null
        }
      />
    );
  };

  return (
    <WalletSectionCard
      id="emails"
      title={t('walletEmails.title')}
      icon={<idme-icon name="mail" color="stone" size="large" />}
      className={walletEmailsStyle.card}
    >
      {defaultEmailError && (
        <div className={walletEmailsStyle.defaultEmailAlert}>
          <WalletAlert
            variant="error"
            onDismiss={handleDismissDefaultEmailError}
            dismissLabel={t('walletEmails.dismissError')}
          >
            {t(defaultEmailErrorKey(defaultEmailError))}
          </WalletAlert>
        </div>
      )}
      {fetchError && (
        <div className={walletEmailsStyle.fetchErrorAlert}>
          <WalletAlert variant="error">
            <p className={walletEmailsStyle.fetchErrorBody}>
              {t('walletEmails.fetchErrorBody')}
            </p>
            <button
              type="button"
              data-testid="wallet-emails-fetch-retry"
              className={walletEmailsStyle.fetchErrorRetry}
              onClick={handleRetryFetch}
            >
              {t('walletEmails.fetchErrorRetry')}
            </button>
          </WalletAlert>
        </div>
      )}
      {renderMainContent()}

      {!inline.mode && (
        <div style={{ paddingTop: 'var(--spacing-3xl)' }}>
          <FragnoButton
            hierarchy="primary"
            label={t('walletEmails.addEmailButton')}
            onClick={handleStartAdd}
            size="md"
          />
        </div>
      )}

      <RemoveEmailConfirmModal
        // Force a fresh fragno-modal instance per removal target, so a
        // reused DOM instance never carries stale internal state from a
        // previous attempt into the next one.
        key={emailToRemove?.email_id || 'none'}
        isOpen={removeModalOpen}
        email={emailToRemove}
        emails={emails}
        errorCode={errorCode}
        isOnlyWorkEmail={preCheckIsOnlyWork}
        isRemoving={isRemoving}
        onConfirm={handleConfirmRemove}
        onClose={handleCloseRemoveModal}
      />

      <WalletLockedModal
        isOpen={inline.mode === 'locked'}
        onClose={handleCloseLockedModal}
        onContactSupport={handleContactSupport}
      />
    </WalletSectionCard>
  );
};

export default memo(WalletEmails);
